import CryptoJS from "crypto-js";
import en from "@/i18n/locales/en.json";
import {
  sealData,
  openData,
  isV2DataEnvelope,
  dataEnvelopeGeneration,
  BASE_KEY_GENERATION,
} from "@/utils/encryptionV2";
import {
  getVaultKeyForGeneration,
  getCurrentGeneration,
} from "@/utils/keyring";

// Encryption placeholder strings sourced from i18n so they stay in sync with translations
const ENCRYPTED_PLACEHOLDER = en.commonLabels.encryptedPlaceholder;
const ENCRYPTED_SHORT = en.commonLabels.encryptedShort;
const DECRYPTION_FAILED = en.commonLabels.decryptionFailed;

// Helper functions outside the class (standalone)
const isHexString = (str, length) => {
  return str.length === length && /^[a-fA-F0-9]+$/.test(str);
};

const isBase64String = (str) => {
  return str.length > 20 && /^[A-Za-z0-9+/]*={0,2}$/.test(str);
};

const isWordArray = (value) => {
  return value && value?.words && value?.sigBytes;
};

// Convert a CryptoJS WordArray to a Uint8Array (big-endian, byte-exact).
const wordArrayToUint8Array = (wa) => {
  const { words, sigBytes } = wa;
  const out = new Uint8Array(sigBytes);
  for (let i = 0; i < sigBytes; i++) {
    out[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return out;
};

// Derive the 32-byte XChaCha20 data key from the (already parsed) vault key.
// Deterministic: the same vault key always yields the same data key, so v2
// data written in one session decrypts in the next. Rotating the vault key
// (Tier B) changes this key, which is why rotation re-encrypts all data.
const deriveDataKey = (keyWordArray) =>
  wordArrayToUint8Array(CryptoJS.SHA256(keyWordArray));

const parseBase64OrUtf8 = (key) => {
  try {
    return CryptoJS.enc.Base64.parse(key);
  } catch (error) {
    console.warn("Base64 parsing failed, treating as UTF8", error);
    return CryptoJS.enc.Utf8.parse(key);
  }
};

const processStringKey = (encryptionKey) => {
  // Check for 128-char hex (256-bit key)
  if (isHexString(encryptionKey, 128)) {
    return CryptoJS.enc.Hex.parse(encryptionKey);
  }

  // Check for 64-char hex
  if (isHexString(encryptionKey, 64)) {
    return CryptoJS.enc.Hex.parse(encryptionKey);
  }

  // Check for base64
  if (isBase64String(encryptionKey)) {
    return parseBase64OrUtf8(encryptionKey);
  }

  // Default: treat as UTF8 string
  return CryptoJS.enc.Utf8.parse(encryptionKey);
};

// Helper functions for passphrase validation
const calculateLengthScore = (length, suggestions) => {
  if (length >= 12) return 2;
  if (length >= 8) return 1;
  
  suggestions.push("Use at least 8 characters");
  return 0;
};

const calculateCharacterScore = (hasLower, hasUpper, hasNumber, hasSpecial) => {
  let score = 0;
  if (hasLower) score += 1;
  if (hasUpper) score += 1;
  if (hasNumber) score += 1;
  if (hasSpecial) score += 1;
  return score;
};

const determineStrength = (score) => {
  if (score >= 7) return "strong";
  if (score >= 5) return "medium";
  return "weak";
};

const generateSuggestions = (hasLower, hasUpper, hasNumber, hasSpecial, wordCount) => {
  const suggestions = [];

  if (!hasLower && !hasUpper) {
    suggestions.push("Include both upper and lowercase letters");
  }

  if (!hasNumber) {
    suggestions.push("Include at least one number");
  }

  if (!hasSpecial && wordCount < 4) {
    suggestions.push("Include special characters or use a longer passphrase");
  }

  return suggestions;
};

export class EncryptionService {
  constructor(encryptionKey) {
    this.originalKey = encryptionKey;
    this.isInitialized = false;
    this.lastError = null;
    try {
      this.encryptionKey = this.processEncryptionKey(encryptionKey);
      // 32-byte key for the v2 (XChaCha20-Poly1305) data envelope.
      this.dataKey = deriveDataKey(this.encryptionKey);
      // The generation this instance's key belongs to. Call sites construct
      // with the CURRENT vault key, so new writes are tagged with the current
      // generation; a rotation in progress reads older generations via the
      // keyring (see dataKeyForGeneration / legacyKeyForGeneration).
      this.generation = getCurrentGeneration() ?? BASE_KEY_GENERATION;
      // Lazy cache of derived keys for OTHER generations (rotation window).
      this._genDataKeys = new Map();
      this._genLegacyKeys = new Map();
      this.isInitialized = true;

      // Validate the key immediately
      this.validateKeyOnInit();
    } catch (error) {
      console.error("EncryptionService initialization failed:", error);
      this.lastError = error;
      this.isInitialized = false;
    }
  }

  processEncryptionKey(encryptionKey) {
    if (!encryptionKey) {
      throw new Error("Encryption key is required");
    }

    // Handle string format
    if (typeof encryptionKey === "string") {
      return processStringKey(encryptionKey);
    }

    // Handle WordArray format
    if (isWordArray(encryptionKey)) {
      return encryptionKey;
    }

    throw new Error("Unsupported encryption key format");
  }

  // Resolve the 32-byte v2 data key for a given generation. The instance's own
  // generation uses the already-derived dataKey; others are looked up in the
  // keyring and cached. Returns null if the keyring doesn't hold that generation.
  dataKeyForGeneration(gen) {
    if (gen === this.generation) return this.dataKey;
    if (this._genDataKeys.has(gen)) return this._genDataKeys.get(gen);
    const vaultKey = getVaultKeyForGeneration(gen);
    const dk = vaultKey ? deriveDataKey(processStringKey(vaultKey)) : null;
    this._genDataKeys.set(gen, dk);
    return dk;
  }

  // Resolve the legacy AES-CBC key (a WordArray) for a generation. Legacy data
  // only ever exists at the base generation; this covers reading it after the
  // vault key has since rotated forward.
  legacyKeyForGeneration(gen) {
    if (gen === this.generation) return this.encryptionKey;
    if (this._genLegacyKeys.has(gen)) return this._genLegacyKeys.get(gen);
    const vaultKey = getVaultKeyForGeneration(gen);
    const wa = vaultKey ? processStringKey(vaultKey) : null;
    this._genLegacyKeys.set(gen, wa);
    return wa;
  }

  validateKeyOnInit() {
    try {
      const testResult = this.testEncryption("init-test");
      if (!testResult) {
        console.warn("Key validation failed during initialization");
        this.lastError = new Error("Key validation failed");
      }
      return testResult;
    } catch (error) {
      this.lastError = error;
      return false;
    }
  }

  encrypt(plaintext) {
    if (!this.isInitialized) {
      throw new Error(
        "EncryptionService not properly initialized: " + this.lastError?.message
      );
    }

    if (!plaintext) {
      console.warn("Empty plaintext provided");
      return plaintext;
    }

    if (typeof plaintext !== "string") {
      console.warn("Non-string plaintext provided, converting");
      plaintext = String(plaintext);
    }

    try {
      // v2: authenticated XChaCha20-Poly1305 with a fresh per-secret nonce,
      // in a self-describing envelope tagged with the current vault-key
      // generation. New writes are always v2; legacy AES-CBC ciphertext is
      // still readable by decrypt() below.
      const result = sealData(this.dataKey, plaintext, this.generation);

      if (!result || result.length < 20) {
        throw new Error("Encryption failed - invalid result format");
      }
      return result;
    } catch (error) {
      console.error("Encryption failed:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack?.split("\n").slice(0, 3).join("\n"),
        plaintextLength: plaintext?.length,
        keyInfo: this.getKeyInfo(),
      });
      throw new Error(`Encryption failed: ${error.message}`);
    }
  }

  decrypt(encryptedText) {
    if (!this.isInitialized) {
      throw new Error(
        "EncryptionService not properly initialized: " + this.lastError?.message
      );
    }
    // Handle empty or placeholder inputs
    if (!encryptedText || typeof encryptedText !== "string") {
      return encryptedText;
    }

    const trimmedText = encryptedText.trim();

    // Skip decrypting placeholder strings
    const placeholders = [
      ENCRYPTED_PLACEHOLDER,
      ENCRYPTED_SHORT,
      DECRYPTION_FAILED,
      "[Encryption service failed]",
      "",
    ];

    if (placeholders.includes(trimmedText)) {
      return encryptedText;
    }

    // v2 envelope: authenticated XChaCha20-Poly1305. Pick the data key for the
    // generation this blob was sealed under (a rotation may leave older
    // generations around). A thrown error here means the tag failed (wrong key
    // or tampering), not a legacy-format fallback.
    if (isV2DataEnvelope(trimmedText)) {
      try {
        const gen = dataEnvelopeGeneration(trimmedText);
        const dataKey = this.dataKeyForGeneration(gen);
        if (!dataKey) {
          console.error("No vault key for generation", gen);
          return "[Wrong passphrase or corrupted data]";
        }
        return openData(dataKey, trimmedText);
      } catch (error) {
        console.error("v2 data decryption failed:", error);
        return "[Wrong passphrase or corrupted data]";
      }
    }

    // Basic validation of encrypted data format (legacy AES-CBC)
    if (!this.isValidEncryptedData(trimmedText)) {
      console.warn("Input doesn't look like valid encrypted data");
      return ENCRYPTED_PLACEHOLDER;
    }

    try {
      // Parse the base64 combined data
      let combined;
      try {
        combined = CryptoJS.enc.Base64.parse(trimmedText);
      } catch (parseError) {
        console.error("Base64 parsing failed:", parseError);
        return "[Invalid base64 format]";
      }

      // Validate minimum size (16 bytes IV + minimum encrypted data)
      if (combined.sigBytes < 32) {
        console.error("Combined data too small:", combined.sigBytes);
        return "[Encrypted data too small]";
      }

      // Extract IV (first 16 bytes = 4 words)
      const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4), 16);

      // Extract ciphertext (remaining bytes)
      const ciphertextWords = combined.words.slice(4);
      const ciphertextSigBytes = combined.sigBytes - 16;
      const ciphertext = CryptoJS.lib.WordArray.create(
        ciphertextWords,
        ciphertextSigBytes
      );
      if (ciphertextSigBytes <= 0) {
        console.error("No ciphertext data found");
        return "[No ciphertext data]";
      }

      // Perform decryption. Legacy AES-CBC data only exists at the base
      // generation; after a rotation the base key comes from the keyring.
      let decrypted;
      try {
        const legacyKey =
          this.legacyKeyForGeneration(BASE_KEY_GENERATION) || this.encryptionKey;
        decrypted = CryptoJS.AES.decrypt(
          CryptoJS.lib.CipherParams.create({ ciphertext: ciphertext }),
          legacyKey,
          {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
          }
        );
      } catch (decryptError) {
        console.error("AES decryption failed:", decryptError);
        return "[AES decryption failed]";
      }
      if (decrypted.sigBytes <= 0) {
        console.warn("Decryption resulted in empty data - likely wrong key");
        return "[Wrong passphrase or corrupted data]";
      }

      // Convert to UTF8 string
      let result;
      try {
        result = decrypted.toString(CryptoJS.enc.Utf8);
      } catch (conversionError) {
        console.error("UTF8 conversion failed:", conversionError);
        return "[UTF8 conversion failed]";
      }

      if (!result || result.length === 0) {
        console.warn(
          "Decryption resulted in empty string - possibly wrong key or corrupted data"
        );
        return "[Wrong passphrase or corrupted data]";
      }
      return result;
    } catch (error) {
      console.error("Decryption failed:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack?.split("\n").slice(0, 3).join("\n"),
        encryptedTextPreview: encryptedText?.substring(0, 50) + "...",
        keyInfo: this.getKeyInfo(),
      });
      return DECRYPTION_FAILED + " - " + error.message + "]";
    }
  }

  // Enhanced test method with comprehensive validation
  testEncryption(testString = "Hello, World! Test 123") {
    if (!this.isInitialized) {
      console.error("Test failed - service not initialized");
      return false;
    }

    try {
      // Test encryption
      const encrypted = this.encrypt(testString);

      if (!encrypted || encrypted.length < 20) {
        console.error("Test failed - encryption returned invalid result");
        return false;
      }

      // Test decryption
      const decrypted = this.decrypt(encrypted);

      // Verify round-trip
      const isWorking = testString === decrypted;

      if (!isWorking) {
        console.error("Test failed - Round-trip comparison:");
        console.error("  Original:", testString);
        console.error("  Decrypted:", decrypted);
        console.error("  Encrypted:", encrypted.substring(0, 100) + "...");
      }

      return isWorking;
    } catch (error) {
      console.error("Encryption test failed:", error);
      return false;
    }
  }

  // Enhanced validation for encrypted data
  isValidEncryptedData(data) {
    if (!data || typeof data !== "string") {
      return false;
    }

    const trimmed = data.trim();

    // v2 envelopes are the current format — accept them outright (they contain
    // dots and base64url chars that the legacy base64 check below would reject).
    if (isV2DataEnvelope(trimmed)) {
      return true;
    }

    // Check for placeholder strings
    const placeholders = [
      ENCRYPTED_PLACEHOLDER,
      ENCRYPTED_SHORT,
      DECRYPTION_FAILED,
    ];

    if (placeholders.some((placeholder) => trimmed.includes(placeholder))) {
      return false;
    }

    // Must be valid base64 (legacy AES-CBC)
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(trimmed)) {
      return false;
    }

    try {
      // Should be parseable as base64
      const decoded = CryptoJS.enc.Base64.parse(trimmed);

      // Should have at least 32 bytes (16 for IV + 16 minimum for encrypted data)
      return decoded.sigBytes >= 32;

    } catch (error) {
      console.warn("Failed to validate encrypted data:", error);
      return false;
    }
  }

  // Comprehensive key information for debugging
  getKeyInfo() {
    return {
      isInitialized: this.isInitialized,
      hasOriginalKey: !!this.originalKey,
      originalKeyType: typeof this.originalKey,
      originalKeyLength: this.originalKey?.length || 0,
      originalKeyPreview: this.originalKey?.toString().substring(0, 20) + "...",
      hasProcessedKey: !!this.encryptionKey,
      processedKeyWords: this.encryptionKey?.words?.length || 0,
      processedKeySigBytes: this.encryptionKey?.sigBytes || 0,
      processedKeyString:
        this.encryptionKey?.toString().substring(0, 20) + "...",
      lastError: this.lastError?.message,
      canEncrypt: this.isInitialized && !!this.encryptionKey,
    };
  }

  // Method to re-initialize with a new key
  reinitialize(newEncryptionKey) {
    this.originalKey = newEncryptionKey;
    this.isInitialized = false;
    this.lastError = null;

    try {
      this.encryptionKey = this.processEncryptionKey(newEncryptionKey);
      this.dataKey = deriveDataKey(this.encryptionKey);
      this.generation = getCurrentGeneration() ?? BASE_KEY_GENERATION;
      this._genDataKeys = new Map();
      this._genLegacyKeys = new Map();
      this.isInitialized = true;

      return this.validateKeyOnInit();
    } catch (error) {
      console.error("EncryptionService reinitialization failed:", error);
      this.lastError = error;
      this.isInitialized = false;
      return false;
    }
  }

  // Batch encrypt multiple values
  batchEncrypt(dataMap) {
    if (!this.isInitialized) {
      throw new Error("EncryptionService not properly initialized");
    }

    const results = {};
    const errors = {};

    for (const [key, value] of Object.entries(dataMap)) {
      try {
        if (value && typeof value === "string" && value.trim()) {
          results[key] = this.encrypt(value);
        } else {
          results[key] = value;
        }
      } catch (error) {
        console.error(`Failed to encrypt ${key}:`, error);
        errors[key] = error.message;
        results[key] = value;
      }
    }

    return {
      results,
      errors: Object.keys(errors).length > 0 ? errors : null,
      success: Object.keys(errors).length === 0,
    };
  }

  // Batch decrypt multiple values
  batchDecrypt(dataMap) {
    if (!this.isInitialized) {
      throw new Error("EncryptionService not properly initialized");
    }
    const results = {};
    const errors = {};

    for (const [key, value] of Object.entries(dataMap)) {
      try {
        if (this.isValidEncryptedData(value)) {
          results[key] = this.decrypt(value);
        } else {
          results[key] = value;
        }
      } catch (error) {
        console.error(`Failed to decrypt ${key}:`, error);
        errors[key] = error.message;
        results[key] = DECRYPTION_FAILED;
      }
    }

    return {
      results,
      errors: Object.keys(errors).length > 0 ? errors : null,
      success: Object.keys(errors).length === 0,
    };
  }
}

// Standalone utility functions
export function formatEncryptionKey(keyData) {
  if (!keyData) {
    throw new Error("Key data is required");
  }

  if (typeof keyData === "string") {
    // Hex string (128 chars for 256-bit key)
    if (keyData.length === 128 && /^[a-fA-F0-9]+$/.test(keyData)) {
      return keyData;
    }

    // Shorter hex string (64 chars)
    if (keyData.length === 64 && /^[a-fA-F0-9]+$/.test(keyData)) {
      return keyData;
    }

    // Base64 string
    if (keyData.length > 20 && /^[A-Za-z0-9+/]*={0,2}$/.test(keyData)) {
      return keyData;
    }

    // Regular string - hash it to create consistent key
    if (keyData.length >= 8) {
      const hashed = CryptoJS.SHA256(keyData);
      return hashed.toString();
    }

    throw new Error("Key string too short (minimum 8 characters)");
  }

  // If it's already processed, return as-is
  return keyData;
}

// Utility to validate passphrase strength
export function validatePassphraseStrength(passphrase) {
  if (!passphrase || typeof passphrase !== "string") {
    return { valid: false, reason: "Passphrase is required" };
  }

  const length = passphrase.length;
  const hasLower = /[a-z]/.test(passphrase);
  const hasUpper = /[A-Z]/.test(passphrase);
  const hasNumber = /\d/.test(passphrase);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(passphrase);
  const wordCount = passphrase.split(/\s+/).length;

  const suggestions = [];
  
  let score = 0;
  score += calculateLengthScore(length, suggestions);
  score += calculateCharacterScore(hasLower, hasUpper, hasNumber, hasSpecial);
  
  if (wordCount >= 4) score += 2;

  const strength = determineStrength(score);
  const additionalSuggestions = generateSuggestions(
    hasLower,
    hasUpper,
    hasNumber,
    hasSpecial,
    wordCount
  );

  return {
    valid: score >= 4,
    strength,
    score,
    suggestions: [...suggestions, ...additionalSuggestions],
    details: {
      length,
      hasLower,
      hasUpper,
      hasNumber,
      hasSpecial,
      wordCount,
    },
  };
}
